import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Form, FormikHelpers } from 'formik';

import { Button } from 'components/Input/Button';
import { TypingAnimation } from 'components/Animations';
import { InputError } from 'components/ErrorBoundary';
import TextField from '../../Input/TextField';

import { useConversation } from 'hooks/conversation';
import { useCustomer } from 'hooks/customer';
import { useWaitTimes } from 'hooks/useWaitTimes';
import { useVendorCommand } from 'hooks/useVendorCommand';

import { getWaitTimeText } from './utils';
import { getFirstName, getLastName } from 'utils/name';
import { isLastMessage, isMessageRead } from 'utils/twilio';
import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';

import { NameFormProps, NameFormValuesType } from './types';
import { nameFormInitialValues, unknownWaitTimeText, validationSchema } from './content';
import { ACTIONS } from 'constants/hook';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import { StyledNameFormContainer, ButtonGroup } from './styles';
import { htmlId } from 'utils/html';
import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_gated_flow_name_form *
 */
const NameForm = (props: NameFormProps) => {
  const { form, twilioMessageIndex } = props;

  const { conversationState } = useConversation();
  const { dispatchCustomerAction } = useCustomer();
  const { showBoundary } = useErrorBoundary();
  const { runVendorCommandAsync } = useVendorCommand();
  const waitTimesQuery = useWaitTimes();

  const [displayText, setDisplayText] = useState(unknownWaitTimeText);

  useEffect(() => {
    if (waitTimesQuery.isFetched && waitTimesQuery.data) {
      const { waitTimeInMinutes = 0 } = waitTimesQuery.data.waitTimes.chat;
      setDisplayText(getWaitTimeText(waitTimeInMinutes));
    }
  }, [waitTimesQuery.isFetched]);

  const onSubmit = async (
    values: NameFormValuesType,
    helpers: FormikHelpers<NameFormValuesType>,
  ) => {
    helpers.setSubmitting(true);
    await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOWUP_NAME, {
      form_id: props.form.id,
      twilio_message_id: conversationState.messages[twilioMessageIndex].sid,
      name: values.name,
    });
    helpers.setSubmitting(false);
    helpers.setStatus('submitted');

    dispatchCustomerAction({
      type: ACTIONS.CHANGE,
      payload: {
        userInfo: { firstName: getFirstName(values.name), lastName: getLastName(values.name) },
      },
    });
  };

  const onSubmitError = (error: Error, helpers: FormikHelpers<NameFormValuesType>) => {
    helpers.setSubmitting(false);
    helpers.setErrors(error);
    const err = new InputError(
      `Critical issue encountered while submitting Name Form. Info: ${String(error)}`,
    );
    return showBoundary(err);
  };

  const initialName = form.info?.name || '';
  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const animate = isLatestMessage && !isMessageRead(twilioMessageIndex, conversationState);
  const commonAnimationProps = { animate, showIndicator: true };

  if (animate && waitTimesQuery.isLoading) {
    return <TypingAnimation />;
  }

  return (
    <StyledNameFormContainer id={htmlId('name-form-container')}>
      <FormWrapper
        id={FORM_IDS.NAME_FORM}
        initialValues={{ ...nameFormInitialValues, name: initialName }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        onError={onSubmitError}
        validateOnBlur={false}
        enableReinitialize
      >
        {({ handleChange, isValidating, isSubmitting, dirty, isValid, status }) => {
          const inputDisabled = form?.isCompleted || isSubmitting || status === 'submitted';
          const submitDisabled = inputDisabled || !isValid || isValidating || !dirty;
          const submitHidden = initialName || status === 'submitted';

          return (
            <Form>
              <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
                <PlainTextMessage text={displayText} />
              </WithDelayedAnimation>
              <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                <TextField
                  id='name'
                  name='name'
                  placeholder='Name'
                  label='Name'
                  onChange={handleChange}
                  disabled={inputDisabled}
                  autoComplete='name'
                />
              </WithDelayedAnimation>
              {!submitHidden && (
                <WithDelayedAnimation relativeIndex={2} {...commonAnimationProps}>
                  <ButtonGroup>
                    <Button
                      disabled={submitDisabled}
                      variant='primary'
                      priority='primary'
                      type='submit'
                    >
                      Submit
                    </Button>
                  </ButtonGroup>
                </WithDelayedAnimation>
              )}
            </Form>
          );
        }}
      </FormWrapper>
    </StyledNameFormContainer>
  );
};

export default NameForm;
